// ComandaService.js - Servicio optimizado para impresión de comandas usando Electron
//const { ipcRenderer } = window.require('electron');
const ipcRenderer = window.electron ? window.electron.ipcRenderer : null;
const electronApi = window.electron ? window.electron.ipcRenderer : null;
/**
 * Clase mejorada para gestionar la impresión de comandas usando Electron
 * Versión optimizada para trabajar con el verificador de impresoras
 */
class ComandaService {
  constructor() {
    // Cache para impresoras disponibles
    this.impresorasDisponibles = null;
    this.configuracionCargada = false;
    this.configuracionImpresoras = {};
    this.ultimoEstado = {};
    this.debug = true; // Habilitar para logs detallados

    // Control de verificación para evitar redundancias
    this.verificacionEnProceso = false;
    this.ultimaVerificacion = null;
    this.tiempoCacheMax = 5 * 60 * 1000; // 5 minutos entre verificaciones completas

    // Memoria de método de impresión exitoso por impresora (evita cadena de fallback)
    this.metodoExitosoPorImpresora = {};
  }

  /**
   * Método de registro para facilitar la depuración
   * @param {string} mensaje - Mensaje a registrar
   * @param {any} datos - Datos adicionales (opcional)
   */
  log(mensaje, datos) {
    if (this.debug) {
      if (datos) {
        console.log(`[ComandaService] ${mensaje}`, datos);
      } else {
        console.log(`[ComandaService] ${mensaje}`);
      }
    }
  }

  /**
   * Inicializa el servicio, cargando la configuración de impresoras
   * @returns {Promise<boolean>} - True si la inicialización fue exitosa
   */
  async initialize() {
    try {
      this.log("Iniciando servicio de impresión");

      // Evitar inicializaciones múltiples
      if (this.verificacionEnProceso) {
        this.log("Inicialización en progreso, esperando...");
        return true;
      }

      this.verificacionEnProceso = true;

      try {
        // Obtener lista de impresoras disponibles primero para asegurar que funciona
        const impresoras = await this.getImpresorasDisponibles(true);

        if (!impresoras || impresoras.length === 0) {
          this.log("⚠️ No se encontraron impresoras en el sistema");
        } else {
          this.log(`✅ Se encontraron ${impresoras.length} impresoras en el sistema`);
        }

        // Cargar configuración de impresoras (pasar impresoras ya obtenidas para evitar doble fetch)
        const config = await this.cargarConfiguracionImpresoras(impresoras);
        this.configuracionImpresoras = config;
        this.configuracionCargada = true;

        this.log("Servicio de impresión inicializado correctamente");
        return true;
      } finally {
        this.verificacionEnProceso = false;
        this.ultimaVerificacion = Date.now();
      }
    } catch (error) {
      console.error('Error al inicializar servicio de impresión:', error);
      this.verificacionEnProceso = false;
      return false;
    }
  }

  async getSystemPrinters() {
    try {
      return await ipcRenderer.invoke('get-system-printers');
    } catch (error) {
      console.error('Error al obtener impresoras usando PowerShell:', error);
      return [];
    }
  }

  /**
   * Lista las impresoras de Windows en un formato amigable para la UI
   * @returns {Promise<Array>} - Lista formateada de impresoras
   */
  async listarImpresorasWindows() {
    try {
      const impresoras = await this.getImpresorasDisponibles(true);

      if (!impresoras || impresoras.length === 0) {
        this.log("⚠️ No se encontraron impresoras al listar para UI");
        return [];
      }

      // Formar una lista más amigable para la UI
      return impresoras.map(impresora => ({
        id: impresora.name,
        nombre: impresora.name,
        tipo: impresora.esTermica ? 'Térmica' : (impresora.isNetwork ? 'Red' : 'Local'),
        esDefault: impresora.isDefault,
        estado: impresora.status || 'Desconocido',
        ubicacion: impresora.location || 'No especificada'
      }));
    } catch (error) {
      console.error('Error al listar impresoras para UI:', error);
      return [];
    }
  }

  /**
   * Obtiene el listado de impresoras disponibles en el sistema con mejor manejo de errores
   * @param {boolean} forceRefresh - Si true, fuerza la recarga del listado de impresoras
   * @returns {Promise<Array>} - Listado de impresoras
   */
  async getImpresorasDisponibles(forceRefresh = false) {
    try {
      // Limitar frecuencia de verificación para evitar problemas
      const ahora = Date.now();
      const debeRefrescar = forceRefresh ||
        !this.impresorasDisponibles ||
        !this.ultimaVerificacion ||
        (ahora - this.ultimaVerificacion > this.tiempoCacheMax);

      // Si ya tenemos la lista y no se debe refrescar, usar la lista en caché
      if (this.impresorasDisponibles && !debeRefrescar) {
        return this.impresorasDisponibles;
      }

      // Evitar verificaciones simultáneas
      if (this.verificacionEnProceso && !forceRefresh) {
        this.log("Verificación en progreso, usando datos en caché");
        return this.impresorasDisponibles || [];
      }

      this.verificacionEnProceso = true;

      try {
        this.log("Obteniendo listado de impresoras desde el proceso principal");

        // Usar IPC para obtener impresoras del proceso principal de Electron
        let impresoras = [];

        try {
          // Intentar primero con el método principal
          this.log('Llamando a get-all-printers');
          impresoras = await ipcRenderer.invoke('get-all-printers');
          this.log(`Impresoras obtenidas con get-all-printers: ${impresoras ? impresoras.length : 0}`);
        } catch (error) {
          console.error('Error con get-all-printers, intentando método alternativo:', error);
          // Intentar con método alternativo
          this.log('Llamando a get-printers (método alternativo)');
          try {
            impresoras = await ipcRenderer.invoke('get-printers');
            this.log(`Impresoras obtenidas con get-printers: ${impresoras ? impresoras.length : 0}`);
          } catch (secondError) {
            console.error('Error con método alternativo:', secondError);
            // Si ambos métodos fallan, intentar con PowerShell como último recurso
            this.log('Intentando obtener impresoras con PowerShell');
            impresoras = await this.getSystemPrinters();
            this.log(`Impresoras obtenidas con PowerShell: ${impresoras ? impresoras.length : 0}`);
          }
        }

        if (!impresoras || impresoras.length === 0) {
          console.warn("No se encontraron impresoras en el sistema");
          return [];
        }

        // Procesar cada impresora para añadir información adicional
        const impresorasDetalladas = impresoras.map(impresora => ({
          ...impresora,
          esTermica: this.detectarImpresoraTermica(impresora.name),
          tipo: this.determinarTipoImpresora(impresora),
          isDefault: impresora.isDefault || false
        }));

        // Guardar en caché
        this.impresorasDisponibles = impresorasDetalladas;
        this.ultimaVerificacion = ahora;

        // Actualizar información de configuración
        this.actualizarInfoImpresoras(impresorasDetalladas);

        this.log(`Se procesaron ${impresorasDetalladas.length} impresoras`);

        return impresorasDetalladas;
      } finally {
        this.verificacionEnProceso = false;
      }
    } catch (error) {
      console.error('Error crítico al obtener impresoras:', error);
      this.verificacionEnProceso = false;
      return [];
    }
  }

  /**
   * Carga la configuración de impresoras desde Windows con mejor manejo de errores
   * @returns {Promise<Object>} - Configuración de impresoras
   */
  async cargarConfiguracionImpresoras(impresorasPrecargadas = null) {
    try {
      this.log("Cargando configuración de impresoras");

      // Usar impresoras precargadas si están disponibles, evitar doble fetch
      const impresoras = impresorasPrecargadas || await this.getImpresorasDisponibles(true);
      const configuracion = {};

      if (!impresoras || impresoras.length === 0) {
        throw new Error("No se encontraron impresoras para configurar");
      }

      // Configurar la impresora predeterminada como "DEFAULT"
      const impresoraDefault = impresoras.find(imp => imp.isDefault);
      if (impresoraDefault) {
        configuracion['DEFAULT'] = {
          modo: 'system',
          nombreImpresora: impresoraDefault.name,
          opciones: {
            fontSize: 1.0,
            cortar: true,
            caracteresPorLinea: 42,
            silent: true
          }
        };
      }

      // Añadir configuración para todas las impresoras del sistema usando sus nombres reales
      // No alteramos los nombres para mantener la consistencia con el sistema
      impresoras.forEach(impresora => {
        const nombreImpresora = impresora.name;

        // Agregar la impresora con su nombre exacto como clave
        if (!configuracion[nombreImpresora]) {
          configuracion[nombreImpresora] = {
            modo: 'system',
            nombreImpresora: nombreImpresora,
            opciones: {
              fontSize: 1.0,
              cortar: this.detectarImpresoraTermica(nombreImpresora), // Solo cortar si es térmica
              caracteresPorLinea: this.detectarImpresoraTermica(nombreImpresora) ? 42 : 80,
              silent: true
            }
          };
        }
      });

      // Si no hay configuración, usar al menos una predeterminada de ejemplo
      if (Object.keys(configuracion).length === 0) {
        console.warn('No se encontraron impresoras en Windows, usando configuración de ejemplo');
        configuracion['EJEMPLO'] = {
          modo: 'system',
          nombreImpresora: 'Microsoft Print to PDF', // Impresora común en Windows
          opciones: {
            fontSize: 1.0,
            cortar: false,
            caracteresPorLinea: 80,
            silent: true
          }
        };
      }

      this.log('Configuración de impresoras cargada:', configuracion);
      return configuracion;
    } catch (error) {
      console.error('Error al cargar configuración de impresoras:', error);

      // En caso de error, devolver una configuración mínima
      return {
        DEFAULT: {
          modo: 'system',
          nombreImpresora: 'Microsoft Print to PDF',
          opciones: {
            fontSize: 1.0,
            cortar: false,
            caracteresPorLinea: 80,
            silent: true
          }
        }
      };
    }
  }

  /**
   * Detecta si una impresora es térmica basada en su nombre
   * @param {string} nombreImpresora - Nombre de la impresora
   * @returns {boolean} - True si es una impresora térmica
   */
  detectarImpresoraTermica(nombreImpresora) {
    if (!nombreImpresora) return false;

    const nombreLower = nombreImpresora.toLowerCase();
    // Patrones comunes de nombres de impresoras térmicas
    const patronesTermicas = [
      'thermal', 'térmica', 'termica',
      'epson tm', 'tm-', 'pos',
      'ticket', 'receipt', 'recibo',
      'esc/pos', 'escpos',
      'bixolon', 'star', 'cocina', 'barra'
    ];

    return patronesTermicas.some(patron => nombreLower.includes(patron));
  }

  /**
   * Determina el tipo de impresora basado en sus características
   * @param {Object} impresora - Objeto con información de la impresora
   * @returns {string} - Tipo de impresora ('termica', 'network', 'normal')
   */
  determinarTipoImpresora(impresora) {
    // Si sabemos que es térmica
    if (this.detectarImpresoraTermica(impresora.name)) {
      return 'termica';
    }

    // Verificar el tipo basado en otras características
    const isNetwork = impresora.isNetwork ||
      (typeof impresora.status === 'string' && impresora.status.includes('NETWORK')) ||
      (typeof impresora.location === 'string' &&
        (impresora.location.includes('\\\\') || impresora.location.includes('http')));

    if (isNetwork) {
      return 'network';
    }

    // Por defecto, considerarla como impresora normal
    return 'normal';
  }

  /**
   * Actualiza la información de las impresoras configuradas con datos reales del sistema
   * @param {Array} impresoras - Lista de impresoras del sistema
   */
  actualizarInfoImpresoras(impresoras) {
    // Recorrer las impresoras configuradas y añadir información del sistema si existe
    for (const [nombre, config] of Object.entries(this.configuracionImpresoras)) {
      if (config.modo === 'system') {
        // Buscar la impresora en la lista del sistema
        const impresoraEncontrada = impresoras.find(
          imp => imp.name === config.nombreImpresora
        );

        if (impresoraEncontrada) {
          this.configuracionImpresoras[nombre].infoSistema = impresoraEncontrada;
          this.configuracionImpresoras[nombre].disponible = true;
        } else {
          this.configuracionImpresoras[nombre].disponible = false;
        }
      }
    }
  }

  /**
   * Busca una impresora por su nombre exacto
   * @param {string} nombre - Nombre de la impresora a buscar
   * @returns {Promise<Object|null>} - Impresora encontrada o null
   */
  async getImpresoraPorNombre(nombre) {
    const impresoras = await this.getImpresorasDisponibles();
    return impresoras.find(imp => imp.name === nombre);
  }

  /**
   * Verifica el estado de una impresora (configurable o directa de Windows)
   * @param {string} nombreImpresora - Nombre de la impresora
   * @returns {Promise<Object>} - Estado de la impresora
   */
  async verificarEstadoImpresora(nombreImpresora) {
    try {
      // Limitar frecuencia de verificación (30 segundos de caché)
      const ahora = Date.now();
      if (this.ultimoEstado[nombreImpresora] &&
        this.ultimoEstado[nombreImpresora].timestamp &&
        (ahora - this.ultimoEstado[nombreImpresora].timestamp < 30000)) { // 30 segundos
        return this.ultimoEstado[nombreImpresora];
      }

      if (!this.configuracionCargada) {
        await this.initialize();
      }

      // Si es la impresora configurada, verificarla según su modo
      if (this.configuracionImpresoras[nombreImpresora]) {
        const configImpresora = this.configuracionImpresoras[nombreImpresora];

        if (configImpresora.modo === 'system') {
          return this.verificarImpresoraSystem(nombreImpresora, configImpresora);
        } else if (configImpresora.modo === 'network') {
          return this.verificarImpresoraRed(
            configImpresora.conexion.ip,
            configImpresora.conexion.puerto
          );
        } else {
          return {
            disponible: false,
            online: false,
            error: 'Modo de impresora no soportado',
            mensaje: `El modo "${configImpresora.modo}" no está soportado`
          };
        }
      }

      // Si no está en la configuración, verificar directamente
      const resultado = await this.verificarImpresoraDirecta(nombreImpresora);

      // Guardar el estado para futuras consultas
      this.ultimoEstado[nombreImpresora] = {
        ...resultado,
        timestamp: ahora
      };

      return resultado;
    } catch (error) {
      console.error(`Error al verificar estado de impresora ${nombreImpresora}:`, error);
      return {
        disponible: false,
        online: false,
        error: 'Error de comunicación',
        mensaje: `Error al comunicarse con la impresora: ${error.message}`
      };
    }
  }

  /**
   * Verifica una impresora que no está en nuestra configuración
   * @param {string} nombreImpresora - Nombre de la impresora
   * @returns {Promise<Object>} - Estado de la impresora
   */
  async verificarImpresoraDirecta(nombreImpresora) {
    // Buscar la impresora en el sistema
    const impresora = await this.getImpresoraPorNombre(nombreImpresora);

    if (!impresora) {
      return {
        disponible: false,
        online: false,
        error: 'Impresora no encontrada',
        mensaje: `La impresora "${nombreImpresora}" no está disponible en el sistema Windows`
      };
    }

    // Obtener estado detallado de la impresora usando IPC
    try {
      const estadoDetallado = await ipcRenderer.invoke('get-printer-status', nombreImpresora);
      return {
        disponible: true,
        online: estadoDetallado.online,
        error: estadoDetallado.error || null,
        mensaje: estadoDetallado.mensaje || 'Impresora disponible',
        detalles: estadoDetallado
      };
    } catch (error) {
      console.error(`Error al obtener estado detallado de ${nombreImpresora}:`, error);
      // Si falla, devolver un estado básico
      return {
        disponible: true,
        online: true, // Asumimos que está online ya que existe en el sistema
        error: null,
        mensaje: 'Estado básico: impresora disponible en el sistema',
      };
    }
  }

  /**
   * Verifica el estado de una impresora del sistema
   * @param {string} nombreConfiguracion - Nombre en la configuración (COCINA, BARRA)
   * @param {Object} configImpresora - Configuración de la impresora
   * @returns {Promise<Object>} - Estado de la impresora
   */
  async verificarImpresoraSystem(nombreConfiguracion, configImpresora) {
    // Recargar impresoras si es necesario
    if (!this.impresorasDisponibles) {
      await this.getImpresorasDisponibles(true);
    }

    // Verificar si existe en el sistema
    const impresoraEncontrada = this.impresorasDisponibles.find(
      imp => imp.name === configImpresora.nombreImpresora
    );

    if (!impresoraEncontrada) {
      return {
        disponible: false,
        online: false,
        error: 'Impresora no encontrada',
        mensaje: `La impresora "${configImpresora.nombreImpresora}" no está disponible en el sistema`
      };
    }

    // Guardar último estado conocido
    this.ultimoEstado[nombreConfiguracion] = {
      timestamp: Date.now(),
      disponible: true,
      online: true,
      mensaje: 'Impresora disponible en el sistema'
    };

    return this.ultimoEstado[nombreConfiguracion];
  }

  /**
   * Verifica el estado de una impresora de red
   * @param {string} ip - Dirección IP de la impresora
   * @param {number} puerto - Puerto de la impresora
   * @returns {Promise<Object>} - Estado de la impresora
   */
  async verificarImpresoraRed(ip, puerto) {
    try {
      // Usar el handler de IPC para probar la conexión
      const resultado = await ipcRenderer.invoke('test-network-printer', {
        ip,
        port: puerto
      });

      return {
        disponible: resultado.success,
        online: resultado.success,
        error: resultado.error || null,
        mensaje: resultado.mensaje
      };
    } catch (error) {
      return {
        disponible: false,
        online: false,
        error: 'Error de conexión',
        mensaje: `No se pudo conectar a la impresora en ${ip}:${puerto}`
      };
    }
  }

  /**
   * Resuelve una ruta UNC (\\IP\share) al nombre local de Windows si está instalada.
   * Si no encuentra coincidencia, devuelve la ruta UNC tal cual para que Windows la resuelva.
   */
  async resolverNombreImpresora(nombre) {
    if (!nombre || typeof nombre !== 'string' || !nombre.startsWith('\\\\')) {
      return nombre;
    }

    const impresoras = await this.getImpresorasDisponibles();
    if (!impresoras || impresoras.length === 0) return nombre;

    const nombreLower = nombre.toLowerCase();

    // 1) Match exacto con la ruta UNC completa (máquina cliente que la agregó como red)
    const exacta = impresoras.find(imp => imp.name && imp.name.toLowerCase() === nombreLower);
    if (exacta) return exacta.name;

    // 2) Extraer último segmento: \\IP\share → "share"
    const partes = nombre.split('\\').filter(Boolean);
    const shareName = partes[partes.length - 1];
    if (!shareName) return nombre;
    const shareLower = shareName.toLowerCase();

    // 3) Match por nombre exacto del share (caso: máquina servidor que la comparte localmente)
    const porShare = impresoras.find(imp => imp.name && imp.name.toLowerCase() === shareLower);
    if (porShare) return porShare.name;

    // 4) Match por portName/location que contenga la ruta UNC o el share
    const porPuerto = impresoras.find(imp =>
      (imp.portName && imp.portName.toLowerCase().includes(nombreLower)) ||
      (imp.location && imp.location.toLowerCase().includes(nombreLower)) ||
      (imp.portName && imp.portName.toLowerCase() === shareLower)
    );
    if (porPuerto) return porPuerto.name;

    // 5) No encontrada localmente: devolver UNC y dejar que Windows la resuelva
    return nombre;
  }

  /**
   * Imprime una comanda en la impresora especificada
   * @param {Array} productos - Lista de productos a imprimir
   * @param {Object} mesa - Datos de la mesa
   * @param {Object} mesero - Datos del mesero
   * @param {String} fecha - Fecha formateada
   * @param {String} hora - Hora formateada
   * @param {String} impresora - Nombre de la impresora a utilizar
   * @returns {Promise<Object>} - Resultado de la operación
   */
  async imprimirComanda(productos, mesa, mesero, fecha, hora, nombreImpresora, infoPedido = {}) {
    try {
      // Resolver ruta UNC al nombre local de Windows si aplica
      const nombreResuelto = await this.resolverNombreImpresora(nombreImpresora);
      if (nombreResuelto !== nombreImpresora) {
        this.log(`UNC resuelto: "${nombreImpresora}" → "${nombreResuelto}"`);
        nombreImpresora = nombreResuelto;
      }

      this.log(`Preparando impresión en impresora ${nombreImpresora}...`);
      this.log(`Info pedido recibido:`, infoPedido);

      // Verificación rápida: si la impresora ya está en la configuración cargada,
      // asumir que está online (la impresión fallará rápido si no lo está).
      // Solo hacer verificación completa si NO está en configuración o si el caché dice offline.
      // Saltar verificación para rutas UNC (Get-Printer no funciona con \\IP\share)
      const esUNC = typeof nombreImpresora === 'string' && nombreImpresora.startsWith('\\\\');
      const cachedEstado = this.ultimoEstado[nombreImpresora];
      const cacheValido = cachedEstado && cachedEstado.timestamp && (Date.now() - cachedEstado.timestamp < 30000);

      if (!esUNC && cacheValido && (!cachedEstado.disponible || !cachedEstado.online)) {
        // El caché reciente dice que está offline - verificar de nuevo por si se recuperó
        const estadoImpresora = await this.verificarEstadoImpresora(nombreImpresora);
        if (!estadoImpresora.disponible || !estadoImpresora.online) {
          console.error(`La impresora ${nombreImpresora} no está disponible:`, estadoImpresora);
          return { success: false, message: `Error: ${estadoImpresora.mensaje}` };
        }
      } else if (!esUNC && !cacheValido && !this.configuracionImpresoras[nombreImpresora]) {
        // Impresora desconocida y sin caché - verificar completamente
        const estadoImpresora = await this.verificarEstadoImpresora(nombreImpresora);
        if (!estadoImpresora.disponible || !estadoImpresora.online) {
          console.error(`La impresora ${nombreImpresora} no está disponible:`, estadoImpresora);
          return { success: false, message: `Error: ${estadoImpresora.mensaje}` };
        }
      }
      // Si la impresora está en configuración o caché dice online → skip verificación

      // Buscar la configuración de la impresora
      let configImpresora = this.configuracionImpresoras[nombreImpresora];

      // Si no existe configuración específica, intentar crear una automáticamente
      if (!configImpresora) {
        const impresora = await this.getImpresoraPorNombre(nombreImpresora);

        if (impresora || esUNC) {
          // Crear configuración básica para esta impresora.
          // Para UNC sin enumerar, asumimos térmica (típico caso: \\PC\receipt-share)
          const esTermica = impresora
            ? this.detectarImpresoraTermica(nombreImpresora)
            : this.detectarImpresoraTermica(nombreImpresora) || esUNC;

          configImpresora = {
            modo: 'system',
            nombreImpresora: nombreImpresora,
            opciones: {
              fontSize: 1.0,
              cortar: esTermica,
              caracteresPorLinea: esTermica ? 42 : 80,
              silent: true
            }
          };

          // Guardar para futuras impresiones
          this.configuracionImpresoras[nombreImpresora] = configImpresora;
        } else {
          return {
            success: false,
            message: `La impresora ${nombreImpresora} no está disponible en el sistema`
          };
        }
      }

      // Según el modo, imprimir de forma diferente
      if (configImpresora.modo === 'system') {
        // Impresora del sistema (usando HTML para imprimir)
        return await this.imprimirComandaHTML(productos, mesa, mesero, fecha, hora, configImpresora, infoPedido);
      } else if (configImpresora.modo === 'network') {
        // Impresora de red (usando ESC/POS para impresoras térmicas)
        return await this.imprimirComandaESCPOS(productos, mesa, mesero, fecha, hora, configImpresora, infoPedido);
      }

      return {
        success: false,
        message: `Modo de impresión no soportado: ${configImpresora.modo}`
      };
    } catch (error) {
      console.error(`Error al imprimir comanda en ${nombreImpresora}:`, error);
      return {
        success: false,
        message: `Error al imprimir comanda: ${error.message}`
      };
    }
  }

  /**
  * Función mejorada con fallback para impresión HTML
  * @param {Array} productos - Lista de productos
  * @param {Object} mesa - Datos de la mesa
  * @param {Object} mesero - Datos del mesero
  * @param {string} fecha - Fecha formateada
  * @param {string} hora - Hora formateada
  * @param {Object} configImpresora - Configuración de la impresora
  * @returns {Promise<Object>} - Resultado de la operación
  */
  async imprimirComandaHTML(productos, mesa, mesero, fecha, hora, configImpresora, infoPedido = {}) {
    // Test del método térmico
    /* window.electron.ipcRenderer.invoke('print-thermal-receipt', {
       printerName: 'coci', // o el nombre real de tu impresora
       textContent: 'PRUEBA TÉRMICA\n================\nProducto 1 x2\nProducto 2 x1\n================\nTotal: $100.00',
       silent: true
     })
       .then(result => console.log('✅ Térmica funciona:', result))
       .catch(error => console.log('❌ Térmica falla:', error));
     try {
       console.log('🧪 Testeando comunicación IPC...');
 
       const testResult = await window.electron.ipcRenderer.invoke('test-ipc', {
         test: true,
         timestamp: new Date().toISOString()
       });
 
       console.log('✅ Test IPC exitoso:', testResult);
       return testResult;
     } catch (error) {
       console.log('❌ Test IPC falló:', error);
       return { success: false, error: error.message };
     }*/





    try {
      this.log(`Generando HTML para impresión en ${configImpresora.nombreImpresora}`);

      // Generar el contenido HTML para la comanda
      const htmlContent = this.generarHTMLComanda(productos, mesa, mesero, fecha, hora, configImpresora, infoPedido);

      if (!htmlContent || htmlContent.trim().length === 0) {
        throw new Error('No se pudo generar el contenido HTML de la comanda');
      }

      this.log(`HTML generado correctamente. Longitud: ${htmlContent.length} caracteres`);

      // ARREGLO: Usar electronApi en lugar de ipcRenderer (igual que en biometricoAPI)
      if (!electronApi) {
        throw new Error('electronApi no está disponible. Esta función requiere la aplicación de escritorio Electron.');
      }

      this.log(`Enviando impresión HTML a ${configImpresora.nombreImpresora}`);

      // Detectar si es impresora térmica
      const nombreLower = configImpresora.nombreImpresora.toLowerCase();
      const esTermica = ['barra', 'cocina', 'pos', 'thermal', 'receipt', 'ticket', 'epson tm', 'tm-'].some(
        keyword => nombreLower.includes(keyword)
      );

      if (esTermica) {
        this.log('🔥 Detectada impresora térmica, usando método directo optimizado');
      }

      // MÉTODO 1: print-thermal-direct (para térmicas) o print-document (para normales)
      const handlerName = esTermica ? 'print-thermal-direct' : 'print-document';

      // === FAST PATH: Si ya sabemos qué método funciona para esta impresora, usarlo directamente ===
      const metodoConocido = this.metodoExitosoPorImpresora[configImpresora.nombreImpresora];
      if (metodoConocido) {
        try {
          this.log(`⚡ Usando método conocido ${metodoConocido} para ${configImpresora.nombreImpresora}`);
          const params = metodoConocido === 'print-receipt'
            ? { printer: configImpresora.nombreImpresora, content: this.convertirHTMLaTexto(productos, mesa, mesero, fecha, hora), cut: true }
            : metodoConocido === 'print-html'
              ? { printer: configImpresora.nombreImpresora, content: htmlContent, silent: true }
              : { printerName: configImpresora.nombreImpresora, htmlContent, silent: true, options: esTermica ? {} : { printBackground: true, margin: { marginType: 'none' } } };

          const resultadoRapido = await Promise.race([
            electronApi.invoke(metodoConocido, params),
            new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout')), 6000))
          ]);
          if (resultadoRapido && resultadoRapido.success) {
            this.log(`✅ Impresión rápida exitosa con ${metodoConocido}`);
            return resultadoRapido;
          }
        } catch (e) {
          this.log(`⚠️ Método conocido ${metodoConocido} falló, probando cadena completa...`);
          delete this.metodoExitosoPorImpresora[configImpresora.nombreImpresora];
        }
      }

      // === CADENA DE FALLBACK (con timeouts reducidos) ===
      try {
        this.log(`Intentando método ${handlerName}...`);

        const resultado1 = await Promise.race([
          electronApi.invoke(handlerName, {
            printerName: configImpresora.nombreImpresora,
            htmlContent: htmlContent,
            silent: true,
            options: esTermica ? {} : {
              printBackground: true,
              margin: { marginType: 'none' }
            }
          }),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error(`Timeout en ${handlerName}`)), 6000)
          )
        ]);

        if (resultado1 && resultado1.success) {
          this.log(`✅ Impresión exitosa con ${handlerName} en ${configImpresora.nombreImpresora}`);
          this.metodoExitosoPorImpresora[configImpresora.nombreImpresora] = handlerName;
          return resultado1;
        } else {
          throw new Error(resultado1?.error || `${handlerName} falló sin mensaje específico`);
        }

      } catch (error1) {
        this.log(`❌ Error con ${handlerName}: ${error1.message}`);

        // Fast-fail: si la impresora no existe, no intentar más métodos
        if (error1.message && (error1.message.includes('no encontrada') || error1.message.includes('not found'))) {
          throw new Error(`Impresora "${configImpresora.nombreImpresora}" no encontrada en el sistema`);
        }

        // MÉTODO 2: print-html (fallback)
        try {
          this.log('Intentando método print-html (fallback)...');

          const resultado2 = await Promise.race([
            electronApi.invoke('print-html', {
              printer: configImpresora.nombreImpresora,
              content: htmlContent,
              silent: true
            }),
            new Promise((_, reject) =>
              setTimeout(() => reject(new Error('Timeout en print-html')), 5000)
            )
          ]);

          if (resultado2 && resultado2.success) {
            this.log(`✅ Impresión exitosa con print-html en ${configImpresora.nombreImpresora}`);
            this.metodoExitosoPorImpresora[configImpresora.nombreImpresora] = 'print-html';
            return resultado2;
          } else {
            throw new Error(resultado2?.error || 'print-html falló');
          }

        } catch (error2) {
          this.log(`❌ Error con print-html: ${error2.message}`);

          // MÉTODO 3: print-receipt (fallback para térmicas)
          try {
            this.log('Intentando método print-receipt (térmicas)...');

            const contenidoTexto = this.convertirHTMLaTexto(productos, mesa, mesero, fecha, hora);

            const resultado3 = await Promise.race([
              electronApi.invoke('print-receipt', {
                printer: configImpresora.nombreImpresora,
                content: contenidoTexto,
                cut: true
              }),
              new Promise((_, reject) =>
                setTimeout(() => reject(new Error('Timeout en print-receipt')), 4000)
              )
            ]);

            if (resultado3 && resultado3.success) {
              this.log(`✅ Impresión exitosa con print-receipt en ${configImpresora.nombreImpresora}`);
              this.metodoExitosoPorImpresora[configImpresora.nombreImpresora] = 'print-receipt';
              return resultado3;
            } else {
              throw new Error(resultado3?.error || 'print-receipt falló');
            }

          } catch (error3) {
            this.log(`❌ Error con print-receipt: ${error3.message}`);

            // Si es impresion silenciosa (ej: AutoPrintService), no abrir ventana del navegador
            if (infoPedido && infoPedido.silentOnly) {
              this.log('Modo silentOnly: omitiendo ventana del navegador');
              const errorFinal = `Métodos de impresión silenciosos fallaron:\n` +
                `1. ${handlerName}: ${error1.message}\n` +
                `2. print-html: ${error2.message}\n` +
                `3. print-receipt: ${error3.message}`;
              throw new Error(errorFinal);
            }

            // MÉTODO 4: Mostrar ventana de impresión del navegador (último recurso)
            try {
              this.log('Intentando impresión con ventana del navegador...');

              const resultado4 = await this.imprimirConVentanaNavegador(htmlContent);

              if (resultado4.success) {
                this.log(`✅ Impresión iniciada con ventana del navegador`);
                return resultado4;
              } else {
                throw new Error('Ventana del navegador falló');
              }

            } catch (error4) {
              this.log(`❌ Error con ventana del navegador: ${error4.message}`);

              const errorFinal = `Todos los métodos de impresión fallaron:\n` +
                `1. ${handlerName}: ${error1.message}\n` +
                `2. print-html: ${error2.message}\n` +
                `3. print-receipt: ${error3.message}\n` +
                `4. ventana navegador: ${error4.message}`;

              throw new Error(errorFinal);
            }
          }
        }
      }

    } catch (error) {
      console.error('Error crítico al imprimir comanda HTML:', error);
      return {
        success: false,
        message: `Error al imprimir comanda: ${error.message}`
      };
    }
  }

  async testIPC() {
    try {
      console.log('🧪 Testeando comunicación IPC...');

      const testResult = await window.electron.ipcRenderer.invoke('test-ipc', {
        test: true,
        timestamp: new Date().toISOString()
      });

      console.log('✅ Test IPC exitoso:', testResult);
      return testResult;
    } catch (error) {
      console.log('❌ Test IPC falló:', error);
      return { success: false, error: error.message };
    }
  }
  /**
   * Convierte HTML a texto plano para impresoras térmicas
   * @param {Array} productos - Lista de productos
   * @param {Object} mesa - Datos de la mesa
   * @param {Object} mesero - Datos del mesero
   * @param {string} fecha - Fecha formateada
   * @param {string} hora - Hora formateada
   * @returns {string} - Contenido en texto plano
   */
  convertirHTMLaTexto(productos, mesa, mesero, fecha, hora) {
    let contenido = "";

    // Encabezado
    contenido += "=============================\n";
    contenido += "      *** COMANDA ***\n";
    contenido += "=============================\n";

    // Información del pedido
    contenido += `MESERO: ${mesero?.nombre || 'Sin asignar'}\n`;
    contenido += `MESA: ${mesa?.nombre || mesa?.numero || 'N/A'}\n`;
    contenido += `FECHA: ${fecha}\n`;
    contenido += `HORA: ${hora}\n`;
    contenido += "-----------------------------\n";

    // Productos
    if (productos && productos.length > 0) {
      productos.forEach(producto => {
        // Si es separador de persona
        if (producto.esSeparador) {
          contenido += "-----------------------------\n";
          contenido += `  ${producto.nombre}\n`;
          contenido += "-----------------------------\n";
          return;
        }

        const cantidad = producto.cantidad || 1;
        const nombre = producto.nombre || 'Producto sin nombre';

        contenido += `${cantidad} x ${nombre}\n`;

        // Instrucciones (campo textoLibre de observacion_detalle)
        if (producto.instrucciones && producto.instrucciones.trim()) {
          contenido += `  >> ${producto.instrucciones.trim()}\n`;
        }

        // Observaciones
        if (producto.observaciones && producto.observaciones.length > 0) {
          producto.observaciones.forEach(obs => {
            if (obs && obs.trim()) {
              contenido += `  * ${obs.trim()}\n`;
            }
          });
        }
        contenido += "\n";
      });
    } else {
      contenido += "No hay productos\n";
    }

    contenido += "=============================\n";
    contenido += `Impreso: ${new Date().toLocaleString()}\n`;
    contenido += "=============================\n";

    return contenido;
  }

  /**
   * Imprime la comanda en una impresora térmica de red usando ESC/POS
   * @param {Array} productos - Lista de productos
   * @param {Object} mesa - Datos de la mesa
   * @param {Object} mesero - Datos del mesero
   * @param {string} fecha - Fecha formateada
   * @param {string} hora - Hora formateada
   * @param {Object} configImpresora - Configuración de la impresora
   * @returns {Promise<Object>} - Resultado de la operación
   */
  async imprimirComandaESCPOS(productos, mesa, mesero, fecha, hora, configImpresora, infoPedido = {}) {
    try {
      // Generar comandos ESC/POS para la impresora térmica
      const comandos = this.generarComandosESCPOS(productos, mesa, mesero, fecha, hora, configImpresora, infoPedido);

      this.log(`Enviando datos ESC/POS a impresora en ${configImpresora.conexion.ip}:${configImpresora.conexion.puerto}`);

      // ARREGLO: Usar electronApi en lugar de ipcRenderer
      const resultado = await electronApi.invoke('print-raw-network', {
        ip: configImpresora.conexion.ip,
        port: configImpresora.conexion.puerto,
        content: comandos,
        timeout: 5000
      });

      return resultado;
    } catch (error) {
      console.error('Error al imprimir comanda ESC/POS:', error);
      return {
        success: false,
        message: `Error al imprimir ESC/POS: ${error.message}`
      };
    }
  }

  /**
   * Abre el cajón de dinero conectado a la impresora térmica
   * Soporta impresoras de red (TCP) y del sistema (USB/Windows)
   * @param {Object} opciones - { ip, port, printerName, pin }
   * @returns {Promise<Object>} - Resultado de la operación
   */
  async abrirCajon(opciones = {}) {
    try {
      let params = { ...opciones };

      // Si no se pasan opciones, intentar detectar la impresora automáticamente
      if (!params.ip && !params.printerName) {
        console.log('Cajón: auto-detectando impresora...');
        console.log('Cajón: configuraciones disponibles:', Object.keys(this.configuracionImpresoras));

        // Buscar impresora de red configurada
        for (const [nombre, config] of Object.entries(this.configuracionImpresoras)) {
          if (config.conexion && config.conexion.ip) {
            params.ip = config.conexion.ip;
            params.port = config.conexion.puerto || 9100;
            console.log(`Cajón: encontrada impresora de red "${nombre}" -> ${params.ip}:${params.port}`);
            break;
          }
        }

        // Si no hay de red, usar la impresora térmica predeterminada del sistema
        if (!params.ip) {
          const impresoras = await this.getImpresorasDisponibles();
          console.log('Cajón: impresoras del sistema:', impresoras?.map(i => `${i.name}${i.isDefault ? ' [DEFAULT]' : ''}`));
          if (impresoras && impresoras.length > 0) {
            const termica = impresoras.find(imp => this.detectarImpresoraTermica(imp.name));
            const defaultPrinter = impresoras.find(imp => imp.isDefault);
            const impresora = termica || defaultPrinter;
            if (impresora) {
              params.printerName = impresora.name;
              console.log(`Cajón: seleccionada "${params.printerName}" (${termica ? 'térmica' : 'default'})`);
            }
          }
        }
      }

      if (!params.ip && !params.printerName) {
        return { success: false, message: 'No se encontró impresora para abrir el cajón' };
      }

      console.log('Cajón: enviando comando con params:', JSON.stringify(params));
      const resultado = await electronApi.invoke('open-cash-drawer', params);
      console.log('Cajón: resultado:', JSON.stringify(resultado));
      return resultado;
    } catch (error) {
      console.error('Error al abrir cajón:', error);
      return { success: false, message: `Error al abrir cajón: ${error.message}` };
    }
  }

  /**
   * Imprime usando ventana del navegador como último recurso
   * @param {string} htmlContent - Contenido HTML a imprimir
   * @returns {Promise<Object>} - Resultado de la operación
   */
  async imprimirConVentanaNavegador(htmlContent) {
    return new Promise((resolve) => {
      try {
        // Crear una nueva ventana
        const ventanaImpresion = window.open('', '_blank', 'width=400,height=600');

        if (!ventanaImpresion) {
          throw new Error('No se pudo abrir ventana de impresión (bloqueador de popups)');
        }

        // Escribir el contenido HTML
        ventanaImpresion.document.write(htmlContent);
        ventanaImpresion.document.close();

        // Ejecutar impresión inmediatamente sin esperar carga completa
        setTimeout(() => {
          try {
            ventanaImpresion.print();

            // Cerrar ventana después de un tiempo
            setTimeout(() => {
              try {
                ventanaImpresion.close();
              } catch (e) {
                console.warn('No se pudo cerrar ventana de impresión automáticamente');
              }
            }, 1500); // Reducido a 1.5 segundos

            resolve({
              success: true,
              message: 'Ventana de impresión abierta exitosamente'
            });
          } catch (printError) {
            try {
              ventanaImpresion.close();
            } catch (e) {
              // Ignorar error al cerrar
            }
            resolve({
              success: false,
              message: `Error al ejecutar impresión: ${printError.message}`
            });
          }
        }, 200); // Reducido a 200ms para impresión más rápida

      } catch (error) {
        resolve({
          success: false,
          message: `Error creando ventana: ${error.message}`
        });
      }
    });
  }
  /**
   * Genera el HTML para imprimir una comanda
   * @param {Array} productos - Lista de productos
   * @param {Object} mesa - Datos de la mesa
   * @param {Object} mesero - Datos del mesero
   * @param {string} fecha - Fecha formateada
   * @param {string} hora - Hora formateada
   * @param {Object} config - Configuración de la impresora
   * @returns {string} - HTML de la comanda
   */
  generarHTMLComanda(productos, mesa, mesero, fecha, hora, config, infoPedido = {}) {
    // VALIDACIÓN DE PARÁMETROS - Evitar errores de inicialización
    const productosValidados = Array.isArray(productos) ? productos : [];
    const mesaValidada = mesa || {};
    const meseroValidado = mesero || {};
    const configValidada = config || {};
    const opcionesValidadas = configValidada.opciones || {};
    const infoPedidoValidado = infoPedido || {};

    // Tamaños de fuente según configuración
    const fontSize = opcionesValidadas.fontSize || 1.0;
    const fontSizeTitle = Math.min(fontSize * 1.2, 1.5); // Limitar el tamaño del título

    // DATOS SEGUROS PARA EL TEMPLATE
    console.log("🔍 DEBUG ComandaService - mesaValidada:", mesaValidada);
    console.log("🔍 DEBUG ComandaService - meseroValidado:", meseroValidado);
    console.log("🔍 DEBUG ComandaService - infoPedidoValidado:", infoPedidoValidado);

    const nombreMesa = mesaValidada.caption || mesaValidada.nombre || `Mesa ${mesaValidada.numero || mesaValidada.caption || 'N/A'}`;
    const nombreMesero = meseroValidado.nombre || meseroValidado.name || meseroValidado.nombreMesero || 'Sin mesero asignado';
    const fechaSegura = fecha || new Date().toLocaleDateString();
    const horaSegura = hora || new Date().toLocaleTimeString();

    // Extraer datos del pedido
    const tipoPedido = infoPedidoValidado.tipoPedido || 'mesa';
    const nombreCliente = infoPedidoValidado.nombreCliente || '';
    const numeroPedido = infoPedidoValidado.numeroPedido || '';

    // Formatear tipo de pedido para mostrar
    const tiposPedidoTexto = {
      'mesa': 'MESA',
      'llevar': 'PARA LLEVAR',
      'domicilio': 'DOMICILIO',
      'uber': 'UBER EATS',
      'rappi': 'RAPPI',
      'delibot': 'DELIBOT'
    };
    const tipoPedidoTexto = tiposPedidoTexto[tipoPedido] || tipoPedido.toUpperCase();

    console.log("✅ DEBUG ComandaService - nombreMesero extraído:", nombreMesero);
    console.log("✅ DEBUG ComandaService - tipoPedido:", tipoPedido, "nombreCliente:", nombreCliente);

    // Crear el HTML
    let html = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <title>Comanda</title>
      <style>
        @page {
          margin: 0;
          size: 80mm auto;  /* Ancho típico de papel térmico */
        }
        body {
          font-family: monospace;
          font-size: ${fontSize}em;
          margin: 0;
          padding: 4mm;
          width: 72mm;  /* 80mm - márgenes */
        }
        .header {
          text-align: center;
          font-weight: bold;
          font-size: ${fontSizeTitle}em;
          margin-bottom: 3mm;
        }
        .info {
          margin-bottom: 3mm;
        }
        .linea {
          border-top: 1px dashed #000;
          margin: 2mm 0;
        }
        .producto {
          margin-bottom: 1mm;
        }
        .observacion {
          margin-left: 5mm;
          font-style: italic;
        }
        .footer {
          margin-top: 3mm;
          text-align: center;
          font-size: 0.8em;
        }
      </style>
    </head>
    <body>`;

    // Encabezado con tipo de pedido
    html += `
      <div class="header">
        *** PLATILLO NUEVO ***<br>
        <span style="font-size: 1.1em;">[ ${tipoPedidoTexto} ]</span>
      </div>`;

    // Información del pedido
    html += `
      <div class="info">`;

    // Mostrar nombre del cliente si existe
    if (nombreCliente && nombreCliente.trim()) {
      html += `<strong>CLIENTE:</strong> ${nombreCliente}<br>`;
    }

    html += `<strong>MESERO:</strong> ${nombreMesero}<br>`;

    // Para mesa mostrar número de mesa, para otros tipos mostrar número de pedido
    if (tipoPedido === 'mesa') {
      html += `<strong>MESA:</strong> ${nombreMesa}<br>`;
    } else {
      html += `<strong>PEDIDO #:</strong> ${numeroPedido || 'N/A'}<br>`;
    }

    html += `<strong>FECHA:</strong> ${fechaSegura}<br>
        <strong>HORA:</strong> ${horaSegura}
      </div>`;

    // Línea separadora
    html += '<div class="linea"></div>';

    // Productos
    if (productosValidados.length > 0) {
      productosValidados.forEach(producto => {
        // Si es un separador de persona, renderizar como encabezado
        if (producto.esSeparador) {
          html += `
          <div class="linea"></div>
          <div style="text-align:center; font-weight:bold; font-size:1.1em; margin:2mm 0;">
            ${producto.nombre}
          </div>`;
          return;
        }

        // Validar datos del producto para evitar errores
        const cantidad = producto.cantidad || 1;
        const nombre = producto.nombre || 'Producto sin nombre';
        const observaciones = Array.isArray(producto.observaciones) ? producto.observaciones : [];

        html += `
        <div class="producto">
          <strong>${cantidad} x ${nombre}</strong>
        </div>`;

        // Añadir instrucciones si existen (campo textoLibre de observacion_detalle)
        const instrucciones = producto.instrucciones || '';
        if (instrucciones && instrucciones.trim()) {
          html += `
            <div class="observacion" >
              >> ${instrucciones.trim()}
            </div>`;
        }

        // Añadir observaciones si existen
        if (observaciones.length > 0) {
          observaciones.forEach(obs => {
            if (obs && obs.trim()) {
              html += `
            <div class="observacion">
              >> ${obs.trim()}
            </div>`;
            }
          });
        }
      });
    } else {
      html += `
      <div class="producto">
        <em>No hay productos para imprimir</em>
      </div>`;
    }

    // Línea separadora final
    html += '<div class="linea"></div>';

    // Pie de página
    html += `
      <div class="footer">
        *** FIN DE COMANDA ***
      </div>
    </body>
    </html>`;

    return html;
  }

  /**
   * Genera comandos ESC/POS para impresoras térmicas de red
   * @param {Array} productos - Lista de productos
   * @param {Object} mesa - Datos de la mesa
   * @param {Object} mesero - Datos del mesero
   * @param {string} fecha - Fecha formateada
   * @param {string} hora - Hora formateada
   * @param {Object} config - Configuración de la impresora
   * @returns {string} - Comandos ESC/POS en formato binario
   */
  generarComandosESCPOS(productos, mesa, mesero, fecha, hora, config, infoPedido = {}) {
    // Comandos ESC/POS básicos
    const ESC = '\x1B';
    const GS = '\x1D';
    const CENTRO = ESC + 'a' + '\x01';
    const IZQUIERDA = ESC + 'a' + '\x00';
    const NEGRITA_ON = ESC + 'E' + '\x01';
    const NEGRITA_OFF = ESC + 'E' + '\x00';
    const TAMAÑO_NORMAL = GS + '!' + '\x00';
    const TAMAÑO_DOBLE = GS + '!' + '\x11';  // Altura y ancho dobles
    const LINEA_CORTE = GS + 'V' + '\x42' + '\x00';

    // Extraer datos del pedido
    const tipoPedido = infoPedido.tipoPedido || 'mesa';
    const nombreCliente = infoPedido.nombreCliente || '';
    const numeroPedido = infoPedido.numeroPedido || '';

    // Formatear tipo de pedido
    const tiposPedidoTexto = {
      'mesa': 'MESA',
      'llevar': 'PARA LLEVAR',
      'domicilio': 'DOMICILIO',
      'uber': 'UBER EATS',
      'rappi': 'RAPPI',
      'delibot': 'DELIBOT'
    };
    const tipoPedidoTexto = tiposPedidoTexto[tipoPedido] || tipoPedido.toUpperCase();

    // Comenzar con tamaño normal
    let comandos = TAMAÑO_NORMAL;

    // Centrar y poner en negrita el título
    comandos += CENTRO + NEGRITA_ON + TAMAÑO_DOBLE;
    comandos += "PLATILLO NUEVO\n";
    comandos += TAMAÑO_NORMAL;
    comandos += NEGRITA_ON;
    comandos += `[ ${tipoPedidoTexto} ]\n`;
    comandos += NEGRITA_OFF;
    comandos += "\n";

    // Volver a alineación izquierda y quitar negrita
    comandos += IZQUIERDA + NEGRITA_OFF;

    // Información general - mostrar cliente si existe
    if (nombreCliente && nombreCliente.trim()) {
      comandos += `CLIENTE: ${nombreCliente}\n`;
    }
    comandos += `MESERO: ${mesero?.nombre || 'NO ASIGNADO'}\n`;

    // Para mesa mostrar número de mesa, para otros tipos mostrar número de pedido
    if (tipoPedido === 'mesa') {
      comandos += `MESA: ${mesa?.nombre || `Mesa_${mesa?.numero}`}\n`;
    } else {
      comandos += `PEDIDO #: ${numeroPedido || 'N/A'}\n`;
    }

    comandos += `FECHA: ${fecha}\n`;
    comandos += `HORA: ${hora}\n`;

    // Línea separadora
    comandos += "- - - - - - - - - - - - - - - - - - -\n";

    // Productos
    productos.forEach(producto => {
      // Si es separador de persona
      if (producto.esSeparador) {
        comandos += CENTRO + NEGRITA_ON;
        comandos += `${producto.nombre}\n`;
        comandos += NEGRITA_OFF + IZQUIERDA;
        return;
      }

      comandos += NEGRITA_ON;
      comandos += `${producto.cantidad} x ${producto.nombre}\n`;
      comandos += NEGRITA_OFF;

      // Instrucciones (campo textoLibre de observacion_detalle)
      if (producto.instrucciones && producto.instrucciones.trim()) {
        comandos += NEGRITA_ON;
        comandos += `   >> ${producto.instrucciones.trim()}\n`;
        comandos += NEGRITA_OFF;
      }

      // Observaciones
      if (producto.observaciones && producto.observaciones.length > 0) {
        producto.observaciones.forEach(obs => {
          comandos += `   * ${obs}\n`;
        });
      }
    });

    // Línea final
    comandos += "**************************************\n\n";

    // Si se configura corte, agregar comando de corte
    if (config.opciones.cortar) {
      comandos += LINEA_CORTE;
    } else {
      // Si no hay corte, agregar líneas en blanco
      comandos += "\n\n\n\n\n";
    }

    return comandos;
  }

  /**
   * Genera el contenido de texto para la comanda (para logging o preview)
   * @param {Array} productos - Lista de productos a imprimir
   * @param {Object} mesa - Datos de la mesa
   * @param {Object} mesero - Datos del mesero
   * @param {String} fecha - Fecha formateada
   * @param {String} hora - Hora formateada
   * @returns {String} - Texto de la comanda
   */
  generarContenidoComanda(productos, mesa, mesero, fecha, hora) {
    // Encabezado de la comanda
    let contenido = "*********** PLATILLO NUEVO ***********\n";
    contenido += `MESERO: ${mesero?.nombre || 'NO ASIGNADO'}\n`;
    contenido += `MESA: ${mesa?.nombre || `Mesa_${mesa?.numero}`}\n`;
    contenido += `FECHA: ${fecha}\n`;
    contenido += `HORA: ${hora}\n`;
    contenido += "- - - - - - - - - - - - - - - - - - -\n";

    // Detalles de productos
    productos.forEach(producto => {
      contenido += `${producto.cantidad} x ${producto.nombre}\n`;

      // Añadir instrucciones si existen (campo textoLibre de observacion_detalle)
      if (producto.instrucciones && producto.instrucciones.trim()) {
        contenido += `   >> ${producto.instrucciones.trim()}\n`;
      }

      // Añadir observaciones si existen
      if (producto.observaciones && producto.observaciones.length > 0) {
        producto.observaciones.forEach(obs => {
          contenido += `   * ${obs}\n`;
        });
      }
    });

    // Pie de la comanda
    contenido += "**************************************\n";

    return contenido;
  }

  /**
   * Imprime productos nuevos pendientes de una comanda
   * @param {Array} productosPendientes - Productos nuevos pendientes de imprimir
   * @param {Object} infoPedido - Información general del pedido
   * @param {Object} configuracion - Configuración del sistema
   * @returns {Promise<Object>} - Resultado de la operación
   */
  async imprimirProductosPendientes(productosPendientes, infoPedido, configuracion) {
    this.log('🖨️ Iniciando imprimirProductosPendientes');

    // Si no hay productos, no hacer nada
    if (!productosPendientes || productosPendientes.length === 0) {
      this.log('No hay productos pendientes para imprimir');
      return {
        success: true,
        message: "No hay productos pendientes para imprimir"
      };
    }

    try {
      // VALIDAR DATOS DE ENTRADA
      const productosValidados = Array.isArray(productosPendientes) ? productosPendientes : [];
      console.log("infoPedido1111", infoPedido);
      const infoValidada = infoPedido || {};
      const configValidada = configuracion || {};

      this.log(`Productos a procesar: ${productosValidados.length}`);

      // Agrupar productos por impresora
      const productosPorImpresora = {};

      productosValidados.forEach(producto => {
        // Solo procesar productos que deben imprimirse
        if (producto.imprimirCocina !== 1 && producto.imprimirCocina !== '1') {
          this.log(`Producto ${producto.nombre} no está marcado para impresión`);
          return;
        }

        const impresora = producto.impresora || configValidada?.impresoraPorDefecto || 'Local';

        if (!productosPorImpresora[impresora]) {
          productosPorImpresora[impresora] = [];
        }

        productosPorImpresora[impresora].push(producto);
      });

      const impresorasAfectadas = Object.keys(productosPorImpresora);
      this.log(`Impresoras que serán utilizadas: ${impresorasAfectadas.join(', ')}`);

      if (impresorasAfectadas.length === 0) {
        return {
          success: false,
          message: "Ningún producto está configurado para impresión"
        };
      }

      // Fecha y hora actual para la comanda
      const fechaActual = new Date();
      const fechaFormateada = this.formatearFecha(fechaActual);
      const horaFormateada = this.formatearHora(fechaActual);

      // Imprimir en cada impresora
      const resultados = [];

      for (const impresora in productosPorImpresora) {
        const productosImpresora = productosPorImpresora[impresora];

        this.log(`Preparando impresión en ${impresora} para ${productosImpresora.length} productos`);

        // Imprimir comanda para esta impresora
        const resultado = await this.imprimirComanda(
          productosImpresora,
          infoValidada.mesa || {},
          infoValidada.mesero || {},
          fechaFormateada,
          horaFormateada,
          impresora,
          {
            tipoPedido: infoValidada.tipoPedido || 'mesa',
            nombreCliente: infoValidada.nombreCliente || '',
            numeroPedido: infoValidada.numeroPedido || infoValidada.numeroCuenta || ''
          }
        );

        resultados.push({
          impresora,
          resultado,
          productos: productosImpresora.length
        });
      }

      // Verificar si todas las impresiones fueron exitosas
      const todoExitoso = resultados.every(r => r.resultado && r.resultado.success);
      const mensajeFinal = todoExitoso
        ? "Todas las comandas se imprimieron correctamente"
        : "Hubo errores al imprimir algunas comandas";

      this.log(`Resultado final: ${mensajeFinal}`);

      return {
        success: todoExitoso,
        resultados: resultados,
        message: mensajeFinal
      };

    } catch (error) {
      console.error("Error al imprimir productos pendientes:", error);
      return {
        success: false,
        message: `Error general al imprimir comandas: ${error.message}`
      };
    }
  }

  /**
   * Formatea una fecha para mostrar en la comanda
   * @param {Date} fecha - Fecha a formatear
   * @returns {String} - Fecha formateada (DD/MM/YYYY)
   */
  formatearFecha(fecha) {
    const d = new Date(fecha);
    const dia = String(d.getDate()).padStart(2, '0');
    const mes = String(d.getMonth() + 1).padStart(2, '0');
    const anio = d.getFullYear();
    return `${dia}/${mes}/${anio}`;
  }

  /**
   * Formatea una hora para mostrar en la comanda
   * @param {Date} fecha - Fecha de la cual extraer la hora
   * @returns {String} - Hora formateada (HH:MM:SS)
   */
  formatearHora(fecha) {
    const d = new Date(fecha);
    const hora = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    const sec = String(d.getSeconds()).padStart(2, '0');
    return `${hora}:${min}:${sec}`;
  }

  /**
   * Procesa el pago de un pedido
   * @param {Object} datosFacturacion - Datos del pago y facturación
   * @returns {Promise<Object>} - Resultado de la operación
   */
  async procesarPago(datosFacturacion) {
    try {
      this.log('🧾 Procesando pago completo...', datosFacturacion);

      const {
        idPedido,
        subtotal,
        descuentoGlobal,
        descuentoManual,
        total,
        pagos,
        cambio,
        tipoPedido,
        productos,
        infoPedido
      } = datosFacturacion;

      // Validar datos requeridos
      if (!idPedido) {
        throw new Error('ID del pedido es requerido');
      }

      if (!pagos || pagos.length === 0) {
        throw new Error('No se han especificado métodos de pago');
      }

      const API = (await import('../utils/api.js')).default;

      // Calcular totales por método de pago
      const totalEfectivo = pagos.filter(p => p.metodo === 'efectivo').reduce((sum, p) => sum + p.monto, 0);
      const totalTarjeta = pagos.filter(p => p.metodo === 'tarjeta').reduce((sum, p) => sum + p.monto, 0);
      const totalTransferencia = pagos.filter(p => p.metodo === 'transferencia').reduce((sum, p) => sum + p.monto, 0);
      const totalCupon = pagos.filter(p => p.metodo === 'cupon').reduce((sum, p) => sum + p.monto, 0);
      const totalCortesia = pagos.filter(p => p.metodo === 'cortesia').reduce((sum, p) => sum + p.monto, 0);

      // Obtener datos adicionales
      const pagoTransferencia = pagos.find(p => p.metodo === 'transferencia');
      const pagoCupon = pagos.find(p => p.metodo === 'cupon');
      const pagoCortesia = pagos.find(p => p.metodo === 'cortesia');

      // Datos del pago para mesa
      const datosPagoMesa = {
        idPedido: idPedido,
        pedido: infoPedido?.mesa?.numero || '0',
        tipodefpago: 'normal',
        descuento: descuentoGlobal || 0,
        montoTotal: total,
        montoPagado: total,
        montoDescuento: descuentoManual || 0,
        pagoTotal: total,
        pagoEfectivo: totalEfectivo,
        pagoTarjeta: totalTarjeta,
        pagoTransferencia: totalTransferencia,
        pagoCupon: totalCupon,
        pagoCortesia: totalCortesia,
        cambio: cambio || 0,
        comision: 0,
        pagoDelivery: '000000000000',
        propina: 0,
        enviadoServicio: 0,
        usuarioCaja: 'caja1',
        referenciaTransferencia: pagoTransferencia?.datos?.referencia || null,
        codigoCupon: pagoCupon?.datos?.codigoCupon || null,
        motivoCortesia: pagoCortesia?.datos?.razon || null,
        autorizadoPor: pagoCortesia?.datos?.autorizadoPor || null
      };

      // Registrar pago de mesa
      await API.pagos.registrarPago(datosPagoMesa);

      // Marcar pedido como liquidado
      await API.pedidos.marcarComoLiquidado({
        idPedido: idPedido,
        liquidado: 1
      });

      this.log('✅ Pago procesado correctamente');

      return {
        success: true,
        message: 'Pago procesado correctamente'
      };

    } catch (error) {
      console.error('Error al procesar pago:', error);
      throw error;
    }
  }
}

// Exportar una instancia única del servicio
const comandaService = new ComandaService();

// Inicializar el servicio inmediatamente
comandaService.initialize()
  .then(success => {
    if (success) {
      console.log('Servicio de impresión inicializado correctamente');
    } else {
      console.warn('El servicio de impresión se ha inicializado con errores');
    }
  });

export default comandaService;